import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Cog, Zap, Car, Sun, Eye, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ROUTE_PATHS } from '@/lib/index';
import { IMAGES } from '@/assets/images';
import { fadeInUp, staggerContainer, staggerItem } from '@/lib/motion';

const industrialProducts = [
  {
    id: 'solar-profiles',
    title: 'Güneş Enerjisi (Solar) Paneli Profilleri',
    description: 'Yenilenebilir enerji sektörü için özel tasarlanmış, dayanıklı ve hafif profil çözümleri.',
    image: IMAGES.G_NE_ENERJISI_306,
    features: [
      'Yüksek korozyon direnci',
      'UV dayanımı',
      'Hafif yapı',
      'Kolay montaj sistemi',
      'Çevre dostu üretim',
      '25 yıl garanti'
    ],
    applications: [
      'Çatı üstü solar sistemler',
      'Arazi tipi GES projeleri',
      'Endüstriyel tesis çatıları',
      'Konut solar sistemleri'
    ],
    specifications: [
      'Alaşım: 6063-T5/T6',
      'Yüzey: Anodize/Elektrostatik boyama',
      'Sıcaklık dayanımı: -40°C / +85°C',
      'Rüzgar yükü: 200 km/h'
    ]
  },
  {
    id: 'automotive-profiles',
    title: 'Otomotiv ve Taşıma Sektörü Profilleri',
    description: 'Otomotiv endüstrisi için yüksek mukavemet ve hafiflik gerektiren özel profiller.',
    image: IMAGES.OTOMOTIV_305,
    features: [
      'Yüksek mukavemet/ağırlık oranı',
      'Çarpışma güvenliği',
      'Korozyon direnci',
      'Hassas toleranslar',
      'Geri dönüştürülebilirlik',
      'Enerji emilimi'
    ],
    applications: [
      'Araç şasisi',
      'Kapı ve pencere çerçeveleri',
      'Tampon sistemleri',
      'Kamyon kasaları'
    ],
    specifications: [
      'Alaşım: 6061-T6, 6082-T6',
      'Akma dayanımı: 240-290 MPa',
      'Çekme dayanımı: 290-340 MPa',
      'Uzama: %8-12'
    ]
  },
  {
    id: 'machine-profiles',
    title: 'Makine ve Şasi Profilleri',
    description: 'Endüstriyel makineler ve şasi uygulamaları için yüksek hassasiyetli profiller.',
    image: IMAGES.CHASSIS_ALUMINUM_8,
    features: [
      'Yüksek boyutsal kararlılık',
      'Titreşim sönümleme',
      'Kolay işlenebilirlik',
      'Modüler tasarım',
      'Yüksek yük taşıma',
      'Uzun ömür'
    ],
    applications: [
      'CNC tezgahları',
      'Otomasyon sistemleri',
      'Konveyör sistemleri',
      'Endüstriyel robotlar'
    ],
    specifications: [
      'Alaşım: 6060-T66, 6063-T6',
      'Doğrusallık: ±0.5mm/m',
      'Yüzey pürüzlülüğü: Ra 1.6',
      'Sertlik: 95-105 HB'
    ]
  }
];

export default function IndustrialProfiles() {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-24 bg-primary overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={IMAGES.HERO_INDUSTRIAL_1} 
            alt="Endüstriyel Profiller" 
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/80 to-primary/60" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <Link 
              to={ROUTE_PATHS.HOME} 
              className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Ana Sayfaya Dön
            </Link>
            
            <h1 className="text-5xl font-bold text-white mb-6">
              Endüstriyel Profiller
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Otomotivden enerjiye, beyaz eşyadan makine imalatına kadar her sektöre özel teknik profiller. 
              Yüksek performans ve güvenilirlik için mühendislik çözümleri.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="space-y-24"
          >
            {industrialProducts.map((product, index) => (
              <motion.div
                key={product.id}
                variants={staggerItem}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="relative rounded-2xl overflow-hidden group">
                    <img 
                      src={product.image} 
                      alt={product.title}
                      className="w-full h-[400px] object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  </div>
                </div>
                
                <div className={index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}>
                  <h2 className="text-3xl font-bold text-primary mb-4">
                    {product.title}
                  </h2>
                  <p className="text-muted-foreground mb-6 text-lg">
                    {product.description}
                  </p>
                  
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                      <Cog className="w-5 h-5 text-primary" />
                      Özellikler
                    </h3>
                    <ul className="space-y-2">
                      {product.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                          <span className="text-muted-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                      <Eye className="w-5 h-5 text-primary" />
                      Uygulama Alanları
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {product.applications.map((app, idx) => (
                        <span 
                          key={idx}
                          className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium"
                        >
                          {app}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                      <Settings className="w-5 h-5 text-primary" />
                      Teknik Özellikler
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {product.specifications.map((spec, idx) => (
                        <div key={idx} className="text-sm text-muted-foreground bg-muted/30 px-3 py-2 rounded">
                          {spec}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl font-bold text-primary mb-6">
              Endüstriyel Projeniz İçin Özel Profil mi Gerekiyor?
            </h2>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
              R&D ekibimiz, sektörünüzün özel gereksinimlerine uygun profil tasarımları geliştirir. 
              Teknik çizimlerinizi paylaşın, size özel çözüm üretelim.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="px-8">
                <Link to={`${ROUTE_PATHS.CONTACT}#teklif-formu`}>
                  Teknik Destek İsteyin
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="px-8">
                <Link to={ROUTE_PATHS.PRODUCTS}>
                  Tüm Ürünleri Görün
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}